import React, { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { trackConversion, trackEvent } from "@/components/GA.component";

import apiService from "../services/api";

import type { ReactElement } from "react";
import type { Resolver } from "react-hook-form";

export type ContactCategory =
  | "affordable"
  | "builders"
  | "homeowners"
  | "investors";

export const landStatusOptions = ["available", "not_yet_secured"] as const;
export const financingStatusOptions = ["secured", "not_yet_secured"] as const;
export const yesNoOptions = ["yes", "no"] as const;
export const investmentFocusOptions = [
  "Circular Economy",
  "Clean tech",
  "Green Materials & Technology",
  "Sustainable Housing",
  "Modular / Prefab Construction",
  "Community & Affordable Housing",
  "Other",
];
export const investmentRangeOptions = [
  "Less than $250k",
  "between $250k and $1M",
  "between $1M and $2M",
  "$2M+",
];
export const referralSourceOptions = [
  "Google / Online Search",
  "News or Media Coverage",
  "Social Media",
  "Event / Shows",
  "Referral (Friend, Family, or Client)",
  "Other",
];
export const projectTypeOptions = [
  "Single-family",
  "Townhouses",
  "Multiplex",
  "Other",
] as const;

const toArray = (v: unknown) => {
  if (Array.isArray(v)) return v;
  if (v == null) return [];
  if (typeof v === "string") return v ? [v] : [];
  // boolean/number/etc. -> []
  return [];
};

const LocationSchema = z.object({
  country: z.preprocess(
    (v) => v ?? "",
    z.string().min(1, "This field is required"),
  ),
  region: z.preprocess(
    (v) => v ?? "",
    z.string().min(1, "This field is required"),
  ),
  city: z
    .string()
    .trim()
    .min(1, "This field is required")
    .max(100, "Invalid value"),
});

const ReferralSourceSchema = z.enum(
  referralSourceOptions,
  "Please select an option",
);

const ProjectInfoSchema = z
  .object({
    type: z.enum(projectTypeOptions, "Please select an option"),
    typeOther: z.string().trim().optional(),
    count: z.preprocess(
      (v) => v ?? 0,
      z.number("Invalid value").nonnegative("Invalid value"),
    ),
    landStatus: z.enum(landStatusOptions, "Please select an option"),
    financingStatus: z.enum(financingStatusOptions, "Please select an option"),
    requiresFinancing: z.enum(yesNoOptions, "Please select an option"),
    additionalInfo: z.string().trim().min(1, "This field is required"),
  })
  .superRefine((val, ctx) => {
    if (val.type.toLowerCase() === "other" && !val.typeOther) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["typeOther"],
        message: "Please specify...",
      });
    }
  });

const phoneUtil = PhoneNumberUtil.getInstance();

export const PhoneSchema = z
  .string()
  .trim()
  .min(1, "This field is required")
  .refine((v) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(v));
    } catch {
      return false;
    }
  }, "Invalid phone number")
  // normalize to E.164 for storage
  .transform((v) => {
    const n = phoneUtil.parseAndKeepRawInput(v);
    return phoneUtil.format(n, PhoneNumberFormat.E164); // => +15145551234
  });

export const ContactFormAffordableSchema = z
  .object({
    kind: z.literal("affordable"),
    lang: z.enum(["en", "fr"]),
    name: z.string().trim().min(1, "This field is required"),
    email: z.email().trim().min(1, "This field is required"),
    phone: PhoneSchema,
    location: LocationSchema,
    projectInfo: ProjectInfoSchema,
    referralSource: ReferralSourceSchema,
    referralSourceOther: z.string().trim().optional(),
  })
  .superRefine((val, ctx) => {
    if (
      val.referralSource.toLowerCase() === "other" &&
      !val.referralSourceOther
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["referralSourceOther"],
        message: "Please specify...",
      });
    }
  });

export const ContactFormBuildersSchema = z
  .object({
    kind: z.literal("builders"),
    lang: z.enum(["en", "fr"]),
    name: z.string().min(1, "This field is required"),
    company: z.string().trim().min(1, "This field is required"),
    email: z.email().trim().min(1, "This field is required"),
    phone: PhoneSchema,
    location: LocationSchema,
    hasSpecificProject: z.enum(yesNoOptions, "Please select an option"),
    projectInfo: ProjectInfoSchema.optional(),
    note: z.string().trim().optional(),
    referralSource: ReferralSourceSchema,
    referralSourceOther: z.string().trim().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.hasSpecificProject === "yes" && !val.projectInfo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["projectInfo"],
        message: "This field is required",
      });
    } else if (val.hasSpecificProject.toLowerCase() === "no" && !val.note) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["note"],
        message: "This field is required",
      });
    }
    if (
      val.referralSource.toLowerCase() === "other" &&
      !val.referralSourceOther
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["referralSourceOther"],
        message: "Please specify...",
      });
    }
  });

export const ContactFormHomeownersSchema = z
  .object({
    kind: z.literal("homeowners"),
    lang: z.enum(["en", "fr"]),
    name: z.string().min(1, "This field is required"),
    email: z.email().trim().min(1, "This field is required"),
    phone: PhoneSchema,
    location: LocationSchema.optional(),
    hasSpecificProject: z.enum(yesNoOptions, "Please select an option"),
    projectInfo: ProjectInfoSchema.optional(),
    note: z.string().optional(),
    hasLand: z.enum(yesNoOptions, "Please select an option").optional(),
    referralSource: ReferralSourceSchema,
    referralSourceOther: z.string().trim().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.hasSpecificProject.toLowerCase() === "yes") {
      if (!val.location) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["location"],
          message: "This field is required",
        });
      }
      if (!val.hasLand) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["hasLand"],
          message: "This field is required",
        });
      }
      if (!val.projectInfo) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["projectInfo"],
          message: "This field is required",
        });
      }
    } else if (val.hasSpecificProject.toLowerCase() === "no" && !val.note) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["note"],
        message: "This field is required",
      });
    }
    if (
      val.referralSource.toLowerCase() === "other" &&
      !val.referralSourceOther
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["referralSourceOther"],
        message: "Please specify...",
      });
    }
  });

export const ContactFormInvestorsSchema = z
  .object({
    kind: z.literal("investors"),
    lang: z.enum(["en", "fr"]),
    name: z.string().min(3, "This field is required"),
    email: z.email().trim().min(1, "This field is required"),
    company: z.string().trim().min(1, "This field is required"),
    phone: PhoneSchema,
    location: LocationSchema.optional(),
    investmentFocus: z.preprocess(
      toArray,
      z.array(z.enum(investmentFocusOptions)).min(1, "Select at least one"),
    ),
    investmentFocusOther: z.string().trim().optional(),
    investmentRange: z.enum(investmentRangeOptions, "This field is required"),
    note: z.string().trim().min(1, "This field is required"),
    referralSource: ReferralSourceSchema,
    referralSourceOther: z.string().trim().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.investmentFocus.includes("Other") && !val.investmentFocusOther) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["investmentFocusOther"],
        message: "Please describe",
      });
    }
    if (
      val.referralSource.toLowerCase() === "other" &&
      !val.referralSourceOther
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["referralSourceOther"],
        message: "Please specify...",
      });
    }
  });

export const ContactSchema = z.discriminatedUnion("kind", [
  ContactFormAffordableSchema,
  ContactFormBuildersSchema,
  ContactFormHomeownersSchema,
  ContactFormInvestorsSchema,
]);

type ContactSchemaUnion =
  | z.infer<typeof ContactFormAffordableSchema>
  | z.infer<typeof ContactFormBuildersSchema>
  | z.infer<typeof ContactFormHomeownersSchema>
  | z.infer<typeof ContactFormInvestorsSchema>;

export type ContactFormData = z.infer<typeof ContactSchema>;
export type ContactFormAffordableType = z.infer<
  typeof ContactFormAffordableSchema
>;
export type ContactFormBuildersType = z.infer<typeof ContactFormBuildersSchema>;
export type ContactFormHomeownersType = z.infer<
  typeof ContactFormHomeownersSchema
>;
export type ContactFormInvestorsType = z.infer<
  typeof ContactFormInvestorsSchema
>;

export interface ContactContextType {
  openSection: boolean;
  setOpenSection: (value: boolean) => void;
  showConfirmModal: boolean;
  setShowConfirmModal: (value: boolean) => void;
  hasError: boolean;
  setHasError: (value: boolean) => void;
  category?: ContactCategory;
  setCategory: (value?: ContactCategory) => void;
  onSubmit: (data: any) => void;
  submitting: boolean;
  submitted: boolean;
  methods: any;
  cleanup: () => void;
  totalFailedAttempts: number;
}

const ContactInitialState: ContactContextType = {
  openSection: false,
  setOpenSection: () => {},
  showConfirmModal: false,
  setShowConfirmModal: () => {},
  hasError: false,
  setHasError: () => {},
  category: undefined,
  setCategory: () => {},
  onSubmit: () => {},
  submitting: false,
  submitted: false,
  methods: null,
  cleanup: () => {},
  totalFailedAttempts: 0,
};

export const ContactContext =
  React.createContext<ContactContextType>(ContactInitialState);

const schemaByCategory = {
  affordable: ContactFormAffordableSchema,
  builders: ContactFormBuildersSchema,
  homeowners: ContactFormHomeownersSchema,
  investors: ContactFormInvestorsSchema,
} as const;

const makeSwitchingResolver =
  (category?: ContactCategory): Resolver<ContactSchemaUnion> =>
  async (values, ctx, opts) => {
    const schema =
      (schemaByCategory as any)[category ?? ""] ?? ContactFormAffordableSchema;
    return zodResolver(schema)(values, ctx, opts);
  };

export const ContactProvider = ({
  children,
}: {
  children: ReactElement | ReactElement[];
}): ReactElement => {
  const [openSection, setOpenSection] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [category, setCategory] = useState<ContactCategory | undefined>(
    undefined,
  );
  const [totalFailedAttempts, setTotalFailedAttempts] = useState(0);

  const methods = useForm({
    resolver: useMemo(() => makeSwitchingResolver(category), [category]),
    defaultValues: {} as ContactFormData,
    mode: "onBlur",
    shouldUnregister: false,
  });

  const onSubmit = async (data: ContactFormData) => {
    const ok = await methods.trigger();
    setHasError(false);
    setSubmitting(true);
    if (ok && totalFailedAttempts < 11) {
      try {
        await apiService.contact(data);
        setSubmitting(false);
        setSubmitted(true);
        setTotalFailedAttempts(0);
        trackConversion();
        trackEvent("ads_conversion_Submit_lead_form_1", {
          category: data.kind,
        });
        trackEvent("contact_us_click_submit", {
          category: data.kind,
        });
      } catch (error) {
        console.error(error);
        setSubmitting(false);
        setHasError(true);
        setTotalFailedAttempts(totalFailedAttempts + 1);
      }
    } else {
      setSubmitting(false);
    }
  };

  const cleanup = () => {
    setOpenSection(false);
    setSubmitted(false);
    setSubmitting(false);
    setCategory(undefined);
    setHasError(false);
    methods.reset();
  };

  useEffect(() => {
    methods.reset(undefined, { keepValues: true, keepDirtyValues: true });
    methods.setValue("kind", category as ContactCategory, {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [methods, category]);

  return (
    <ContactContext.Provider
      value={{
        openSection,
        setOpenSection,
        showConfirmModal,
        setShowConfirmModal,
        hasError,
        setHasError,
        category,
        setCategory,
        onSubmit,
        submitting,
        submitted,
        methods,
        cleanup,
        totalFailedAttempts,
      }}
    >
      {children}
    </ContactContext.Provider>
  );
};
